/**
 * Core tracing system for capturing and analyzing agent interactions
 */

import { 
  TraceContext, 
  IntentClassificationTrace, 
  OperationTrace, 
  ConversationTrace,
  TraceAnalysis,
  LearningPattern
} from './trace-types';
import { TraceCleanupManager, DEFAULT_RETENTION_POLICY, RetentionPolicy } from './cleanup-manager';
import { writeFile, readFile, mkdir } from 'fs/promises';
import { existsSync } from 'fs';
import { join } from 'path';

export class DezzyTracer {
  private traces: Map<string, ConversationTrace> = new Map();
  private tracePath: string;
  private sessionId: string;
  private cleanupManager: TraceCleanupManager;
  private lastCleanupCheck: Date = new Date();
  
  constructor(projectPath: string, retentionPolicy?: Partial<RetentionPolicy>) {
    this.tracePath = join(projectPath, '.dezzy', 'traces');
    this.sessionId = this.generateSessionId();
    const fullRetentionPolicy = { ...DEFAULT_RETENTION_POLICY, ...retentionPolicy };
    this.cleanupManager = new TraceCleanupManager(this.tracePath, fullRetentionPolicy);
    this.ensureTraceDirectory();
    this.startCleanupSystem();
  }
  
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  private async ensureTraceDirectory(): Promise<void> {
    try {
      if (!existsSync(this.tracePath)) {
        await mkdir(this.tracePath, { recursive: true });
      }
    } catch (error) {
      console.warn('Failed to create trace directory:', error);
    }
  }
  
  /**
   * Initialize cleanup system
   */
  private startCleanupSystem(): void {
    // Start automatic cleanup
    this.cleanupManager.startAutoCleanup();
    
    // Log cleanup policy
    const stats = this.cleanupManager.getCleanupStats();
    console.log('🧹 Trace cleanup initialized:', {
      maxInMemoryTraces: stats.policy.maxInMemoryTraces,
      maxInMemoryAgeDays: stats.policy.maxInMemoryAgeDays,
      cleanupIntervalMinutes: stats.policy.cleanupIntervalMinutes
    });
  }
  
  /**
   * Start tracing a new conversation
   */
  startConversationTrace(conversationId: string, projectPath: string, agentVersion: string): TraceContext {
    return {
      sessionId: this.sessionId,
      conversationId,
      timestamp: new Date(),
      environment: {
        agentVersion,
        projectPath,
        storybookVersion: process.env.STORYBOOK_VERSION
      }
    };
  }
  
  /**
   * Trace intent classification process
   */
  traceIntentClassification(
    context: TraceContext,
    input: { message: string; conversationHistory?: string[] },
    startTime: Date,
    result: {
      intent: string;
      confidence: number;
      extractedEntities: Record<string, any>;
      reasoning?: string;
    },
    classifierType: 'llm' | 'fallback' = 'llm'
  ): IntentClassificationTrace {
    const trace: IntentClassificationTrace = {
      traceId: this.generateTraceId('intent'),
      context,
      input,
      classifier: {
        type: classifierType,
        confidence: result.confidence,
        processingTime: Date.now() - startTime.getTime()
      },
      result,
      alternatives: this.generateAlternativeIntents(input.message, result)
    };
    
    // Log for immediate debugging
    console.log('🔍 Intent Classification Trace:', {
      message: input.message,
      detectedIntent: result.intent,
      confidence: result.confidence,
      entities: result.extractedEntities,
      classifierType: classifierType,
      modelUsed: 'unknown', // result.modelUsed || 'unknown',
      processingTime: trace.classifier.processingTime + 'ms'
    });
    
    return trace;
  }
  
  /**
   * Trace operation execution
   */
  traceOperation(
    context: TraceContext,
    operation: { type: string; target: string; parameters: Record<string, any> },
    startTime: Date,
    endTime: Date,
    result: any
  ): OperationTrace {
    const trace: OperationTrace = {
      traceId: this.generateTraceId('operation'),
      context,
      operation,
      execution: {
        startTime,
        endTime,
        processingTime: endTime.getTime() - startTime.getTime(),
        success: result.success
      },
      result,
      performance: {
        memoryUsage: this.getMemoryUsage(),
        cpuTime: process.cpuUsage().user
      }
    };
    
    // Log for immediate debugging
    console.log('⚙️  Operation Execution Trace:', {
      operation: operation.type,
      target: operation.target,
      success: result.success,
      processingTime: trace.execution.processingTime + 'ms',
      hasStorybookCommand: !!result.storybookCommand,
      hasActionButtons: !!result.actionButtons?.length
    });
    
    return trace;
  }
  
  /**
   * Complete conversation trace
   */
  completeConversationTrace(
    context: TraceContext,
    userMessage: string,
    intentTrace: IntentClassificationTrace,
    operationTrace: OperationTrace,
    formattedResponse: string,
    responseStartTime: Date
  ): ConversationTrace {
    const trace: ConversationTrace = {
      traceId: this.generateTraceId('conversation'),
      context,
      flow: {
        userMessage,
        intentTrace,
        operationTrace,
        responseGeneration: {
          startTime: responseStartTime,
          endTime: new Date(),
          formattedResponse
        }
      },
      outcome: {
        // Will be updated when user provides feedback
        userSatisfaction: 'neutral',
        followUpAction: 'none'
      }
    };
    
    // Store trace for analysis
    this.traces.set(trace.traceId, trace);
    
    // Persist trace
    this.persistTrace(trace);
    
    // Periodic cleanup check (not on every trace to avoid overhead)
    this.checkAndCleanup();
    
    // Log summary for immediate debugging
    console.log('💬 Conversation Trace Complete:', {
      traceId: trace.traceId,
      totalTime: trace.flow.responseGeneration.endTime.getTime() - context.timestamp.getTime() + 'ms',
      intent: intentTrace.result.intent,
      operationSuccess: operationTrace.result.success,
      hasActionButtons: !!operationTrace.result.actionButtons?.length
    });
    
    return trace;
  }
  
  /**
   * Update trace with user outcome (button click, satisfaction, etc.)
   */
  updateTraceOutcome(
    traceId: string, 
    outcome: {
      userSatisfaction?: 'positive' | 'negative' | 'neutral';
      followUpAction?: 'button_click' | 'new_message' | 'navigation' | 'none';
      actionSuccess?: boolean;
    }
  ): void {
    const trace = this.traces.get(traceId);
    if (trace) {
      trace.outcome = { ...trace.outcome, ...outcome };
      this.persistTrace(trace);
      
      console.log('📊 Trace Outcome Updated:', {
        traceId,
        satisfaction: outcome.userSatisfaction,
        followUpAction: outcome.followUpAction,
        actionSuccess: outcome.actionSuccess
      });
    }
  }
  
  /**
   * Analyze traces and extract learning patterns
   */
  async analyzeTraces(timeRange?: { start: Date; end: Date }): Promise<TraceAnalysis> {
    const traces = Array.from(this.traces.values());
    const filteredTraces = timeRange 
      ? traces.filter(t => t.context.timestamp >= timeRange.start && t.context.timestamp <= timeRange.end)
      : traces;
    
    const analysis: TraceAnalysis = {
      analysisId: this.generateTraceId('analysis'),
      timeRange: timeRange || {
        start: new Date(Math.min(...traces.map(t => t.context.timestamp.getTime()))),
        end: new Date(Math.max(...traces.map(t => t.context.timestamp.getTime())))
      },
      metrics: this.calculateMetrics(filteredTraces),
      insights: this.generateInsights(filteredTraces),
      learningPatterns: this.extractLearningPatterns(filteredTraces)
    };
    
    // Save analysis
    await this.persistAnalysis(analysis);
    
    console.log('📈 Trace Analysis Complete:', {
      analysisId: analysis.analysisId,
      tracesAnalyzed: filteredTraces.length,
      successRate: analysis.metrics.successRate,
      avgResponseTime: analysis.metrics.averageResponseTime + 'ms',
      topIntents: analysis.metrics.mostCommonIntents.slice(0, 3)
    });
    
    return analysis;
  }
  
  // Private helper methods
  
  private generateTraceId(type: string): string {
    return `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  private generateAlternativeIntents(message: string, result: any): any[] {
    // Simple alternative intent generation for debugging
    const alternatives = [];
    const lowerMessage = message.toLowerCase();
    
    if (result.intent !== 'SHOW_STORY' && (lowerMessage.includes('show') || lowerMessage.includes('navigate'))) {
      alternatives.push({ intent: 'SHOW_STORY', confidence: 0.7, reason: 'Contains navigation keywords' });
    }
    
    if (result.intent !== 'ANALYZE_STORY' && (lowerMessage.includes('what') || lowerMessage.includes('why'))) {
      alternatives.push({ intent: 'ANALYZE_STORY', confidence: 0.6, reason: 'Contains question keywords' });
    }
    
    return alternatives;
  }
  
  private getMemoryUsage(): number {
    return process.memoryUsage().heapUsed;
  }
  
  private calculateMetrics(traces: ConversationTrace[]) {
    const successfulTraces = traces.filter(t => t.flow.operationTrace.result.success);
    const intentCounts = new Map<string, number>();
    const errorCounts = new Map<string, number>();
    
    traces.forEach(trace => {
      const intent = trace.flow.intentTrace.result.intent;
      intentCounts.set(intent, (intentCounts.get(intent) || 0) + 1);
      
      if (!trace.flow.operationTrace.result.success) {
        const errors = trace.flow.operationTrace.result.errors || ['Unknown error'];
        errors.forEach(error => {
          errorCounts.set(error, (errorCounts.get(error) || 0) + 1);
        });
      }
    });
    
    return {
      totalConversations: traces.length,
      successRate: Math.round((successfulTraces.length / traces.length) * 100) / 100,
      averageResponseTime: Math.round(
        traces.reduce((sum, t) => 
          sum + t.flow.responseGeneration.endTime.getTime() - t.context.timestamp.getTime(), 0
        ) / traces.length
      ),
      intentAccuracy: this.calculateIntentAccuracy(traces),
      mostCommonIntents: Array.from(intentCounts.entries())
        .map(([intent, count]) => ({ intent, count }))
        .sort((a, b) => b.count - a.count),
      errorPatterns: Array.from(errorCounts.entries())
        .map(([error, frequency]) => ({ error, frequency }))
        .sort((a, b) => b.frequency - a.frequency)
    };
  }
  
  private calculateIntentAccuracy(traces: ConversationTrace[]): number {
    // Simple accuracy calculation based on successful operations
    // In a real system, this would compare against human-labeled ground truth
    const successfulIntentOperations = traces.filter(t => 
      t.flow.operationTrace.result.success && 
      t.outcome.userSatisfaction !== 'negative'
    );
    return Math.round((successfulIntentOperations.length / traces.length) * 100) / 100;
  }
  
  private generateInsights(traces: ConversationTrace[]) {
    const performanceBottlenecks = [];
    const misclassificationPatterns = [];
    const userBehaviorPatterns = [];
    const improvementSuggestions = [];
    
    // Analyze performance bottlenecks
    const slowOperations = traces.filter(t => 
      t.flow.operationTrace.execution.processingTime > 1000
    );
    if (slowOperations.length > traces.length * 0.1) {
      performanceBottlenecks.push('Operation execution time exceeds 1s in >10% of cases');
    }
    
    // Analyze misclassification patterns
    const lowConfidenceClassifications = traces.filter(t => 
      t.flow.intentTrace.result.confidence < 0.7 && !t.flow.operationTrace.result.success
    );
    if (lowConfidenceClassifications.length > traces.length * 0.2) {
      misclassificationPatterns.push('Low confidence intent classifications correlated with failures');
    }
    
    // Analyze user behavior
    const buttonClickTraces = traces.filter(t => t.outcome.followUpAction === 'button_click');
    if (buttonClickTraces.length > traces.length * 0.6) {
      userBehaviorPatterns.push('Users prefer action buttons over typing new messages');
    }
    
    // Generate improvement suggestions
    if (performanceBottlenecks.length > 0) {
      improvementSuggestions.push('Optimize operation execution performance');
    }
    if (misclassificationPatterns.length > 0) {
      improvementSuggestions.push('Improve intent classification model training');
    }
    
    return {
      performanceBottlenecks,
      misclassificationPatterns,
      userBehaviorPatterns,
      improvementSuggestions
    };
  }
  
  private extractLearningPatterns(traces: ConversationTrace[]): LearningPattern[] {
    const patterns: LearningPattern[] = [];
    
    // Pattern: Successful intent classification patterns
    const successfulIntents = traces.filter(t => t.flow.operationTrace.result.success);
    const intentGroups = this.groupBy(successfulIntents, t => t.flow.intentTrace.result.intent);
    
    Object.entries(intentGroups).forEach(([intent, traces]) => {
      if (traces.length >= 3) { // Minimum frequency for pattern
        patterns.push({
          patternId: `intent_success_${intent.toLowerCase()}`,
          type: 'intent_classification',
          confidence: traces.reduce((sum, t) => sum + t.flow.intentTrace.result.confidence, 0) / traces.length,
          frequency: traces.length,
          examples: traces.slice(0, 3).map(t => t.flow.userMessage),
          metadata: {
            discoveredAt: new Date(),
            lastSeen: new Date(Math.max(...traces.map(t => t.context.timestamp.getTime()))),
            environments: [...new Set(traces.map(t => t.context.environment.projectPath))]
          }
        });
      }
    });
    
    return patterns;
  }
  
  private groupBy<T, K extends string | number>(array: T[], keyFn: (item: T) => K): Record<K, T[]> {
    return array.reduce((groups, item) => {
      const key = keyFn(item);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
      return groups;
    }, {} as Record<K, T[]>);
  }
  
  private async persistTrace(trace: ConversationTrace): Promise<void> {
    try {
      const filename = join(this.tracePath, `${trace.traceId}.json`);
      await writeFile(filename, JSON.stringify(trace, null, 2));
    } catch (error) {
      console.warn('Failed to persist trace:', error);
    }
  }
  
  private async persistAnalysis(analysis: TraceAnalysis): Promise<void> {
    try {
      const filename = join(this.tracePath, `analysis_${analysis.analysisId}.json`);
      await writeFile(filename, JSON.stringify(analysis, null, 2));
    } catch (error) {
      console.warn('Failed to persist analysis:', error);
    }
  }
  
  /**
   * Periodic cleanup check (called sparingly to avoid overhead)
   */
  private checkAndCleanup(): void {
    const now = new Date();
    const timeSinceLastCheck = now.getTime() - this.lastCleanupCheck.getTime();
    const checkIntervalMs = 5 * 60 * 1000; // Check every 5 minutes
    
    // Only check occasionally to avoid overhead
    if (timeSinceLastCheck > checkIntervalMs || this.traces.size > 1500) {
      this.lastCleanupCheck = now;
      
      // Clean up in-memory traces
      const result = this.cleanupManager.cleanupInMemoryTraces(this.traces);
      
      if (result.removed > 0) {
        console.log(`🧹 In-memory trace cleanup: ${result.removed} traces removed`);
      }
    }
  }
  
  /**
   * Get real-time debugging info including cleanup stats
   */
  getDebugInfo(): any {
    const recentTraces = Array.from(this.traces.values())
      .sort((a, b) => b.context.timestamp.getTime() - a.context.timestamp.getTime())
      .slice(0, 5);
    
    const cleanupStats = this.cleanupManager.getCleanupStats();
    
    return {
      sessionId: this.sessionId,
      totalTraces: this.traces.size,
      recentTraces: recentTraces.map(t => ({
        traceId: t.traceId,
        message: t.flow.userMessage,
        intent: t.flow.intentTrace.result.intent,
        success: t.flow.operationTrace.result.success,
        timestamp: t.context.timestamp
      })),
      cleanup: {
        lastCleanup: cleanupStats.lastCleanup,
        nextCleanupETA: cleanupStats.nextCleanupETA,
        isAutoCleanupRunning: cleanupStats.isAutoCleanupRunning,
        maxInMemoryTraces: cleanupStats.policy.maxInMemoryTraces,
        maxDiskTraces: cleanupStats.policy.maxDiskTraces
      }
    };
  }
  
  /**
   * Force immediate cleanup (for debugging/admin)
   */
  async forceCleanup(): Promise<void> {
    await this.cleanupManager.forceCleanup();
    this.cleanupManager.cleanupInMemoryTraces(this.traces);
  }
  
  /**
   * Update retention policy
   */
  updateRetentionPolicy(policy: Partial<RetentionPolicy>): void {
    this.cleanupManager.updatePolicy(policy);
  }
  
  /**
   * Get detailed information about a specific trace
   */
  async getTraceDetails(traceId: string): Promise<ConversationTrace | null> {
    // First check in-memory traces
    const memoryTrace = this.traces.get(traceId);
    if (memoryTrace) {
      return memoryTrace;
    }
    
    // If not in memory, try to load from disk
    try {
      const tracePath = join(this.tracePath, `${traceId}.json`);
      const traceContent = await readFile(tracePath, 'utf8');
      return JSON.parse(traceContent);
    } catch (error) {
      console.warn(`Trace ${traceId} not found in memory or disk:`, error);
      return null;
    }
  }
  
  /**
   * Graceful shutdown - stop cleanup timers
   */
  shutdown(): void {
    this.cleanupManager.stopAutoCleanup();
    console.log('🛑 Tracer shutdown - cleanup stopped');
  }
}